import {
  GetProviderPatientTranscriptionConsentQuery,
  GetProviderPatientTranscriptionConsentQueryVariables,
  GetProviderTranscriptionConsentQuery,
  GetProviderTranscriptionConsentQueryVariables,
} from '../../generated/graphql';

import { gql, useQuery } from '@apollo/client';
import { useAtomValue, useSetAtom } from 'jotai';

import { logger } from '../../datadog';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { patientConsentAtom, patientConsentUpdateSourceAtom, providerConsentAtom } from '../../session/messages/state';
import { ConsentStatus, ConsentUpdateSource } from '../../video-sdk';
import { inSessionPatientInformationAtom } from '../state';
import { useGetCachedPatientByShortIdCallback } from '../useGetPatientByShortId';
import { useCanProviderUseTranscriptionCallback } from './useCanProviderUseTranscriptionCallback';
import { useStartRecordingCallback } from './useStartRecordingCallback';

export const GET_PROVIDER_PATIENT_CONSENT = gql`
  query GetProviderPatientTranscriptionConsent($patientShortId: String!, $providerShortId: String!) {
    clientTranscriptionConsent(patientShortId: $patientShortId) {
      isOptedIn
      updateSource
    }
    providerTranscriptionConsent(providerShortId: $providerShortId) {
      isOptedIn
      updateSource
    }
  }
`;

export const GET_PROVIDER_CONSENT = gql`
  query GetProviderTranscriptionConsent($providerShortId: String!) {
    providerTranscriptionConsent(providerShortId: $providerShortId) {
      isOptedIn
      updateSource
    }
  }
`;

export function optedInToConsentStatus(optedIn?: boolean): ConsentStatus {
  if (optedIn === undefined) return ConsentStatus.UNDECIDED;

  return optedIn ? ConsentStatus.OPTED_IN : ConsentStatus.OPTED_OUT;
}

export function resolveUpdateSource(updateSource?: string) {
  if (updateSource === ConsentUpdateSource.CLIENT_PORTAL) return ConsentUpdateSource.CLIENT_PORTAL;
  if (updateSource === ConsentUpdateSource.INTAKE_PAPERWORK) return ConsentUpdateSource.INTAKE_PAPERWORK;
  if (updateSource === ConsentUpdateSource.CHECKIN_PAPERWORK) return ConsentUpdateSource.CHECKIN_PAPERWORK;
  if (updateSource === ConsentUpdateSource.PROVIDER_DASHBOARD) return ConsentUpdateSource.PROVIDER_DASHBOARD;
  if (updateSource === ConsentUpdateSource.TELEHEALTH) return ConsentUpdateSource.TELEHEALTH;
  return ConsentUpdateSource.UNKNOWN;
}

/**
 * Automatically fetches/returns the provider and patient consent statuses for
 * transcription. If both have opted in & there is a scheduled appointment, it will also start recording.
 */
export function useGetProviderPatientConsent(providerShortId?: string, patientShortId?: string) {
  const setProviderTranscriptionConsent = useSetAtom(providerConsentAtom);
  const setPatientTranscriptionConsent = useSetAtom(patientConsentAtom);
  const setPatientConsentUpdateSource = useSetAtom(patientConsentUpdateSourceAtom);
  const getCachedPatient = useGetCachedPatientByShortIdCallback();

  const getAppointmentShortId = useAtomCallbackMemoized(
    (get) => get(inSessionPatientInformationAtom)?.inSessionAppointmentShortId,
    [],
  );
  const getCanProviderUseTranscription = useCanProviderUseTranscriptionCallback();

  const startRecording = useStartRecordingCallback();

  const handleRequestSuccess = (data: GetProviderPatientTranscriptionConsentQuery) => {
    if (!providerShortId || !patientShortId) return;

    const providerOptIn = data?.providerTranscriptionConsent?.isOptedIn;
    const patientOptIn = data?.clientTranscriptionConsent?.isOptedIn;
    const patientUpdateSource = resolveUpdateSource(data?.clientTranscriptionConsent?.updateSource);
    const appointmentShortId = getAppointmentShortId();
    const providerConsent = optedInToConsentStatus(providerOptIn);
    const patientConsent = optedInToConsentStatus(patientOptIn);
    // Fail-closed: if cache miss, treat as minor to avoid auto-starting for minors.
    const cachedPatient = getCachedPatient({ patientShortId });
    const isClientUserMinor = cachedPatient?.clientUser?.isMinor ?? true;
    setProviderTranscriptionConsent({
      value: providerConsent,
      lastUpdated: Date.now(),
    });
    setPatientTranscriptionConsent({
      value: patientConsent,
      lastUpdated: Date.now(),
    });
    setPatientConsentUpdateSource({
      value: patientUpdateSource,
      lastUpdated: Date.now(),
    });

    const canUseTranscription = getCanProviderUseTranscription();

    const shouldStartRecording =
      canUseTranscription &&
      providerConsent === ConsentStatus.OPTED_IN &&
      patientConsent === ConsentStatus.OPTED_IN &&
      appointmentShortId &&
      !isClientUserMinor;

    if (shouldStartRecording) {
      logger.info('Starting recording automatically because all conditions are met', {
        providerShortId,
        patientShortId,
        appointmentShortId,
      });
      startRecording();
    } else {
      logger.info(
        'Not starting recording automatically because some conditions are not met (check "conditions" property for specifics)',
        {
          providerShortId,
          patientShortId,
          appointmentShortId,
          conditions: {
            providerCanUseTranscription: canUseTranscription,
            providerHasConsent: providerConsent === ConsentStatus.OPTED_IN,
            patientHasConsent: patientConsent === ConsentStatus.OPTED_IN,
            isClientUserMinor: cachedPatient?.clientUser?.isMinor,
          },
        },
      );
    }
  };

  const { refetch } = useQuery<
    GetProviderPatientTranscriptionConsentQuery,
    Partial<GetProviderPatientTranscriptionConsentQueryVariables>
  >(GET_PROVIDER_PATIENT_CONSENT, {
    variables: { providerShortId, patientShortId },
    skip: !providerShortId || !patientShortId,
    context: { skipAuth: false },
    onCompleted: handleRequestSuccess,
  });

  const providerConsent = useAtomValue(providerConsentAtom).value;
  const patientConsent = useAtomValue(patientConsentAtom).value;
  const patientUpdateSource = useAtomValue(patientConsentUpdateSourceAtom).value;
  return {
    refetch,
    patientConsent,
    providerConsent,
    patientUpdateSource,
  };
}

/**
 * Hook to fetch the provider's transcription consent status.
 */
export function useGetProviderConsent(providerShortId?: string) {
  const { data, loading, error } = useQuery<
    GetProviderTranscriptionConsentQuery,
    Partial<GetProviderTranscriptionConsentQueryVariables>
  >(GET_PROVIDER_CONSENT, {
    variables: { providerShortId },
    skip: !providerShortId,
    context: { skipAuth: false },
    onError: (err) => {
      logger.error('Failed to fetch provider transcription consent', {
        providerShortId,
        error: err.message,
      });
    },
  });

  const providerConsent =
    data !== undefined ? optedInToConsentStatus(data?.providerTranscriptionConsent?.isOptedIn) : undefined;

  return {
    providerConsent,
    loading,
    error,
  };
}
